import * as React from 'react';
import { ReactElement, useEffect, useRef, useState } from 'react';
import Cancel from '../icons/cancel';
import Expand from '../icons/expand';
import { Flex } from './flex';

export interface SelectProps {
  className?: string;
  style?: React.CSSProperties;
  value?: SelectOption;
  label?: string;
  size?: 'default' | 'mini';
  options: SelectOption[];
  disabled?: boolean;
  onChange?: (value: SelectOption) => void;
  onChangeSearch?: (value: string) => void;
  forcePositionBottom?: boolean;
  placeholder?: string;
  error?: string | boolean;
  wrapperStyle?: React.CSSProperties;
  wrapperClassName?: string;
  icon?: ReactElement | string;
  withSearch?: boolean;
  isActive?: boolean;
  onActivate?: () => void;
  searchPlaceholder?: string;
  wrapperId?: string;
}

export interface SelectOption {
  value: string | number | null;
  label: string | ReactElement;
}

export const Select: React.FC<SelectProps> = (props) => {
  const [value, setValue] = useState(props.value);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [ref, setRef] = useState(null);
  const [selectOptions, setSelectOptions] = useState<SelectOption[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setValue(props.value);

    if (props.withSearch) {
      if (props.value && open === false) {
        setSearch((props.value.label as string) ?? '');
      } else if (!props.value) {
        setSearch('');
      }
    }
  }, [props.value]);

  useEffect(() => {
    if (!props.value && props.placeholder) {
      setSearch(props.placeholder);
    }
  }, [props.placeholder]);

  useEffect(() => {
    document.body.addEventListener('click', (e) => {
      e.stopPropagation();
      const target = e.target as HTMLElement;
      if (!target.closest('.select__wrapper') && !target.classList.contains('select__wrapper')) {
        setOpen(false);
      }
    });

    if (props.withSearch && !isSelectFilled && search === '') {
      setSearch(props.placeholder ?? '');
    }
  }, []);

  useEffect(() => {
    if (!props.isActive && open) {
      setOpen(false);
    }
  }, [props.isActive]);

  useEffect(() => {
    if (!props.options) {
      setSelectOptions([]);
    } else if (props.withSearch && search) {
      setSelectOptions(
        props.options.filter((option) => option.label.toString().toLowerCase().includes(search.toLowerCase()))
      );
    } else {
      setSelectOptions(props.options);
    }
  }, [props.options, props.withSearch, search]);

  const isSelectFilled = value && value.value != null && value.value !== '';
  let classNames = `select__wrapper ${props.className} select-size_${props.size ?? 'default'}`;
  if (open) classNames += ' select-opened';
  if (isSelectFilled) classNames += ' select-filled';
  if (props.disabled) classNames += ' select-disabled';
  if (window.innerHeight - ref?.getBoundingClientRect().bottom < 350 && !props.forcePositionBottom) {
    classNames += ' select-open_top';
  }
  if (props.error) classNames += ' select-error';

  const valueElement = props.withSearch ? (
    <Flex align={'center'}>
      <input
        ref={inputRef}
        value={search}
        onChange={(e) => {
          props.onChangeSearch?.(e.target.value);
          setSearch(e.target.value);
          if (!open) setOpen(true);
        }}
        className={'select__search'}
        placeholder={props.searchPlaceholder}
      />
      {search && (
        <div
          onClick={(e) => {
            e.stopPropagation();
            props.onChangeSearch?.('');
            setSearch('');
            inputRef.current?.focus();
            if (props.onChange) {
              props.onChange({ value: null, label: '' });
            }
          }}
          style={{ height: '24px' }}
        >
          <Cancel color={'#00000052'} />
        </div>
      )}
    </Flex>
  ) : (
    <div className={'select__value'}>{isSelectFilled ? value.label : props.placeholder}</div>
  );

  return (
    <div style={props.wrapperStyle} className={props.wrapperClassName} id={props.wrapperId}>
      <div
        onClick={(e) => {
          e.stopPropagation();
          if (!open) {
            props.onActivate?.();
          }
          setOpen(!open);
          if (inputRef.current) {
            inputRef.current.focus();
            const length = inputRef.current.value.length;
            inputRef.current.setSelectionRange(length, length);
          }
        }}
        className={classNames}
        ref={setRef}
        style={props.style}
      >
        <div className={'select__left'}>
          {props.icon && <div className={'text_field__icon'}>{props.icon}</div>}
          <div className={'select__input'}>
            {props.size != 'mini' && <div className={'select__label'}>{props.label}</div>}
            {valueElement}
          </div>
        </div>
        <Expand rotated={open} />
        <div className={'select__options'}>
          {selectOptions.map((option, index) => (
            <div
              className={'select__option'}
              onClick={(e) => {
                e.stopPropagation();
                if (props.onChange) {
                  props.onChange(option);
                } else {
                  setValue(option);
                }
                if (props.withSearch) {
                  setSearch((option.label as string) ?? '');
                }
                setOpen(false);
              }}
              key={index}
            >
              {option.label}
            </div>
          ))}
        </div>
      </div>
      {typeof props.error !== 'boolean' && props.error && (
        <div className={'text_field__bottom_error'}>{props.error}</div>
      )}
    </div>
  );
};
